import {Text, Group, TextInput, createStyles, Box} from '@mantine/core';
import React, {useState} from 'react';

import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import {InputChangeEvent} from 'util/Types';

const useStyles = createStyles(() => ({
  redirectUri: {
    flexGrow: 1,
  },
  redirectUriText: {
    height: '28px',
    lineHeight: '28px',
  },
}));

interface RedirectUriRowProps {
  editing: boolean;
  redirectUri: string;
  onChange: (e: InputChangeEvent) => void;
  onDiscard: () => void;
  onSave: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

function RedirectUriRow(props: RedirectUriRowProps): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <Group position="apart">
      {props.editing ? (
        <>
          <TextInput
            className={classes.redirectUri}
            value={props.redirectUri}
            onChange={props.onChange}
            autoFocus={true}
          />
          <Group spacing="0">
            <Tooltip container="body" placement={TooltipPlacement.LEFT} title="Discard changes">
              <Button
                buttonStyle={ButtonStyle.CIRCLE_ICON}
                size={ButtonSize.SMALL}
                icon={
                  <BenchlingIcon icon="close" altText="Discard changes" className="faded u-danger-pointer" />
                }
                onClick={props.onDiscard}
              />
            </Tooltip>
            <Tooltip container="body" placement={TooltipPlacement.RIGHT} title="Save changes">
              <Button
                buttonStyle={ButtonStyle.CIRCLE_ICON}
                size={ButtonSize.SMALL}
                icon={
                  <BenchlingIcon icon="check" altText="Save changes" className="faded u-primary-pointer" />
                }
                onClick={props.onSave}
              />
            </Tooltip>
          </Group>
        </>
      ) : (
        <>
          <Text className={cx(classes.redirectUri, classes.redirectUriText)} fz="sm">
            {props.redirectUri}
          </Text>
          <Group spacing="0">
            <Tooltip container="body" placement={TooltipPlacement.LEFT} title="Delete">
              <Button
                buttonStyle={ButtonStyle.CIRCLE_ICON}
                size={ButtonSize.SMALL}
                icon={
                  <BenchlingIcon
                    icon="delete"
                    altText={`Delete ${props.redirectUri}`}
                    className="faded u-danger-pointer"
                  />
                }
                onClick={props.onDelete}
              />
            </Tooltip>
            <Tooltip container="body" placement={TooltipPlacement.RIGHT} title="Edit">
              <Button
                buttonStyle={ButtonStyle.CIRCLE_ICON}
                size={ButtonSize.SMALL}
                icon={
                  <BenchlingIcon
                    icon="edit"
                    altText={`Edit ${props.redirectUri}`}
                    className="faded u-primary-pointer"
                  />
                }
                onClick={props.onEdit}
              />
            </Tooltip>
          </Group>
        </>
      )}
    </Group>
  );
}

export interface RedirectUriSectionProps {
  redirectUris: Array<string>;
  handleSetRedirectUris: (redirectUris: Array<string>) => void;
}

interface RedirectUriFormState {
  value: string;
  editingIndex?: number;
}

export default function RedirectUriSection({
  redirectUris,
  handleSetRedirectUris,
}: RedirectUriSectionProps): JSX.Element {
  const [formState, setFormState] = useState<RedirectUriFormState>({value: '', editingIndex: undefined});

  const handleSave = (redirectUri: string, index: number): void => {
    const newUri = redirectUri.trim();
    if (!newUri) {
      return;
    }

    const nextRedirectUris = [...redirectUris.slice(0, index), newUri, ...redirectUris.slice(index + 1)];
    handleSetRedirectUris(nextRedirectUris);
  };
  const handleDelete = (redirectUri: string): void => {
    const nextRedirectUris = redirectUris.filter((uri) => uri !== redirectUri);
    handleSetRedirectUris(nextRedirectUris);
  };

  const headerCell = (
    <Group spacing="0">
      <Text>Redirect URIs</Text>
      <Button
        className="simpleDivTable-addButton"
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        size={ButtonSize.SMALL}
        iconAltText="Add"
        icon="create"
        onClick={() => {
          setFormState({editingIndex: redirectUris.length, value: ''});
        }}
      />
    </Group>
  );

  const getRow = (redirectUri: string, index: number): Row => {
    const editing = index === formState.editingIndex;
    const contents = (
      <RedirectUriRow
        editing={editing}
        redirectUri={editing ? formState.value : redirectUri}
        onChange={(e) => {
          setFormState({editingIndex: index, value: e.target.value});
        }}
        onDiscard={() => {
          setFormState({editingIndex: undefined, value: ''});
        }}
        onEdit={() => {
          setFormState({value: redirectUri, editingIndex: index});
        }}
        onSave={() => {
          handleSave(formState.value, index);
        }}
        onDelete={() => {
          handleDelete(redirectUri);
        }}
      />
    );

    return {id: `row-${index}`, cells: [{key: `cell-${index}`, contents}]};
  };

  const getRows = (): Array<Row> => {
    const includeNewRow = formState.editingIndex === redirectUris.length;
    return [...redirectUris, ...(includeNewRow ? [formState.value] : [])].map((uri, idx) => getRow(uri, idx));
  };

  return (
    <Box w="450px">
      <ReorderableSimpleTable
        headerCells={[{key: 'title', contents: headerCell, cellClassName: 'simpleDivTable-header--title'}]}
        isDraggingDisabled={true}
        noRowsDisplay="No redirect URIs configured"
        rows={getRows()}
      />
    </Box>
  );
}
